/**
 * The app's session plumbing — the bearer-token source for callout-api.
 *
 * Sign-in happens against callout-auth (`web/src/api/auth.ts`); the token it returns is written
 * here and read back by every authenticated request. There is exactly ONE mode now. The old
 * `clerk | dev` split died with Clerk — there is no provider to select between, and no
 * "unconfigured" branch: an app with no token is simply signed out.
 *
 * Storage: `localStorage['callout.devToken']`. The KEY NAME is historic and load-bearing — the
 * browser E2E suites inject a test token under exactly this key
 * (`scripts/render-e2e.mjs`, `tests/e2e/full-pipeline.mjs --token` / `CALLOUT_DEV_TOKEN`), so it
 * is a contract, not a leftover.
 *
 * Session claims (email/name/sub/exp) are read out of the token for DISPLAY and for spotting an
 * obviously dead token. They are never used for an authorization decision — that is the API
 * worker's job, and it verifies the signature against the issuer's JWKS.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { ISSUER, signOut as revokeSession } from './auth';

export const SESSION_TOKEN_KEY = 'callout.devToken';

/**
 * Flag recording that the last session DIED (401, or a token that lapsed while the tab was
 * closed). Without it, a reload cannot tell "never signed in" from "your session was cut off",
 * and the coach would be dropped to a bare sign-in form with no explanation. Cleared by any
 * successful sign-in and by a deliberate sign-out.
 */
const SESSION_ENDED_KEY = 'callout.sessionEnded';

/** Emitted on every session change, so non-React code can react to a fresh token. */
const TOKEN_EVENT = 'callout:token';

/** Treat a token that expires within this window as already dead, rather than sending it. */
const EXPIRY_SKEW_SECONDS = 5;

/**
 * `signed-in` — a usable token is stored.
 * `signed-out` — no token, and none was rejected (a first visit, or the coach signed out).
 * `expired` — a token WAS present and is now dead: past its `exp`, revoked by the issuer
 *             elsewhere, or refused with a 401. This is the state that must not be presented as
 *             a plain "signed out" — it is why the coach's work stopped.
 */
export type SessionStatus = 'signed-in' | 'signed-out' | 'expired';

export interface SessionUser {
  id: string | null;
  email: string | null;
  name: string | null;
}

export interface TokenSource {
  mode: 'session' | 'none';
  getToken: () => Promise<string | null>;
}

export interface Session {
  status: SessionStatus;
  token: string | null;
  user: SessionUser | null;
  /** Revokes at the issuer, then clears local state. Resolves true when revocation confirmed. */
  signOut: () => Promise<boolean>;
}

interface SessionSnapshot {
  status: SessionStatus;
  token: string | null;
  user: SessionUser | null;
}

// --------------------------------------------------------------------------- token claims

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export interface TokenClaims {
  sub?: unknown;
  email?: unknown;
  name?: unknown;
  exp?: unknown;
  iss?: unknown;
}

/** Decodes a JWT payload WITHOUT verifying it. Display/diagnostics only — never a gate. */
export function decodeClaims(token: string): TokenClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = base64UrlDecode(parts[1]);
  if (!json) return null;
  try {
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

/** The coach behind a token, for the header. Derived from unverified claims — display only. */
export function sessionUser(token: string | null): SessionUser | null {
  if (!token) return null;
  const claims = decodeClaims(token);
  if (!claims) return null;
  return {
    id: typeof claims.sub === 'string' ? claims.sub : null,
    email: typeof claims.email === 'string' ? claims.email : null,
    name: typeof claims.name === 'string' ? claims.name : null,
  };
}

/** Epoch seconds the token dies at, when it says so. */
export function sessionExpiry(token: string): number | null {
  const exp = decodeClaims(token)?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp : null;
}

/**
 * An opaque or unparseable token is NOT treated as expired — we cannot know, and guessing would
 * sign a coach out on a token the API might still accept. The 401 from the API is the authority.
 */
function isExpired(token: string): boolean {
  const exp = sessionExpiry(token);
  return exp !== null && exp * 1000 <= Date.now() + EXPIRY_SKEW_SECONDS * 1000;
}

// --------------------------------------------------------------------------- the store

function readStored(): string | null {
  try {
    return window.localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

function persist(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(SESSION_TOKEN_KEY, token);
    else window.localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* storage disabled — the session lives in memory for this tab only */
  }
}

function readEndedFlag(): boolean {
  try {
    return window.localStorage.getItem(SESSION_ENDED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistEnded(ended: boolean): void {
  try {
    if (ended) window.localStorage.setItem(SESSION_ENDED_KEY, '1');
    else window.localStorage.removeItem(SESSION_ENDED_KEY);
  } catch {
    /* storage disabled */
  }
}

function snapshotOf(status: SessionStatus, token: string | null): SessionSnapshot {
  return { status, token, user: status === 'signed-in' ? sessionUser(token) : null };
}

/**
 * A stored-but-dead token is not a session: drop it and say why. A missing token with the
 * "session ended" flag still set means a session died earlier — that stays `expired` until the
 * coach signs in again, instead of silently looking like a first visit.
 */
function hydrate(): SessionSnapshot {
  const stored = readStored();
  if (!stored) return snapshotOf(readEndedFlag() ? 'expired' : 'signed-out', null);
  if (isExpired(stored)) {
    persist(null);
    return snapshotOf('expired', null);
  }
  return snapshotOf('signed-in', stored);
}

let state: SessionSnapshot = snapshotOf('signed-out', null);
const listeners = new Set<() => void>();

function getSnapshot(): SessionSnapshot {
  return state;
}

const SIGNED_OUT_SNAPSHOT = snapshotOf('signed-out', null);
function getServerSnapshot(): SessionSnapshot {
  return SIGNED_OUT_SNAPSHOT;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Applies a change, persists the token (and the "session ended" flag, which always mirrors the
 * status), and wakes every subscriber. Value-compared: no loops.
 */
function apply(next: SessionSnapshot): void {
  if (next.status === state.status && next.token === state.token) return;
  state = next;
  persist(next.status === 'signed-in' ? next.token : null);
  persistEnded(next.status === 'expired');
  for (const listener of listeners) listener();
  try {
    window.dispatchEvent(new Event(TOKEN_EVENT));
  } catch {
    /* no window (tests) — the React subscribers were already notified */
  }
}

// Boot from storage: a token that lapsed while the tab was closed is recognised here, and the
// "session ended" flag is written by apply() because the status is `expired`.
apply(hydrate());

// Another tab signing in/out (or a harness writing the key directly) must land here too.
// NOTE: do NOT re-hydrate from our OWN `callout:token` event — apply() emits it, and reading
// storage back after a 401 would flip the deliberate `expired` state (token already dropped)
// straight back to `signed-out`, hiding why the coach was interrupted. `storage` only fires in
// OTHER tabs, so it is safe.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== SESSION_TOKEN_KEY) return;
    apply(hydrate());
  });
}

/** The raw token, for non-React callers (request headers, uploads). */
export function readSessionToken(): string | null {
  return state.token;
}

/**
 * Stores a freshly minted session token (from `signIn`/`signUp`) or clears the session.
 * A token already past its `exp` is refused rather than stored — the app would only send it and
 * collect a 401.
 */
export function writeSessionToken(token: string | null): void {
  if (!token) {
    apply(SIGNED_OUT_SNAPSHOT);
    return;
  }
  if (isExpired(token)) {
    apply(snapshotOf('expired', null));
    return;
  }
  apply(snapshotOf('signed-in', token));
}

/**
 * Called when the API answered 401: the token we sent is no longer accepted (expired, or revoked
 * by a sign-out somewhere else). Drops it — continuing to send a rejected bearer would be worse
 * than stopping — and records `expired`, so the UI can say why the coach was interrupted instead
 * of showing a bare sign-in form.
 *
 * A 401 on a request that carried no token is NOT an expiry: nothing was signed in to expire.
 */
export function markSessionExpired(): void {
  if (!state.token) return;
  apply(snapshotOf('expired', null));
}

/** The current session, reactive. */
export function useSession(): Session {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const signOut = useCallback(async (): Promise<boolean> => {
    // Read the token at call time (not from the render closure) so a sign-out always revokes the
    // session actually in hand, and revoke BEFORE clearing so the issuer gets a usable bearer.
    const current = readSessionToken();
    const revoked = await revokeSession(current);
    apply(SIGNED_OUT_SNAPSHOT);
    return revoked;
  }, []);

  return useMemo(
    () => ({ status: snapshot.status, token: snapshot.token, user: snapshot.user, signOut }),
    [snapshot, signOut],
  );
}

/**
 * The token source every API call reads. `mode` mirrors the session so components can re-run
 * their loads when a coach signs in; `getToken` always reads live state, so a caller that holds a
 * stale TokenSource object still sends the current token.
 */
export function useTokenSource(): TokenSource {
  const { token } = useSession();
  return useMemo(
    () => ({ mode: token ? ('session' as const) : ('none' as const), getToken: async () => readSessionToken() }),
    [token],
  );
}

/** True when a request made with this source will actually carry a token. */
export function tokenSourceReady(mode: TokenSource['mode'], token: string | null): boolean {
  return mode === 'session' && token !== null;
}

/** The issuer this session belongs to — surfaced in the UI so a mis-pointed build is obvious. */
export { ISSUER };
